import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { FileText, Upload, Download, User, FolderOpen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DocumentUploader = ({ bénéficiaireId }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openCV, setOpenCV] = useState(false);
  const [openAutre, setOpenAutre] = useState(false);
  const [file, setFile] = useState(null);
  const [typeDocument, setTypeDocument] = useState('autre');

  useEffect(() => {
    loadDocuments();
  }, [bénéficiaireId]);

  const loadDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/bénéficiaires/${bénéficiaireId}/documents`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      console.error('Erreur chargement documents', error);
    }
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async (type) => {
    if (!file) {
      toast.error('Veuillez sélectionner un fichier');
      return;
    }

    setLoading(true);
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64 = e.target.result.split(',')[1];
        const token = localStorage.getItem('token');
        
        await axios.post(`${API_URL}/api/documents`, {
          apprenant_id: bénéficiaireId,
          nom_fichier: file.name,
          type_document: type === 'cv' ? 'cv' : typeDocument,
          contenu_base64: base64
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        toast.success('Document téléversé avec succès');
        setOpenCV(false);
        setOpenAutre(false);
        setFile(null);
        setTypeDocument('autre');
        loadDocuments();
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error('Erreur de téléversement');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const base64Data = doc.contenu_base64;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.nom_fichier);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Erreur de téléchargement');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Document supprimé');
      loadDocuments();
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  // Séparer les documents par type
  const cvDocuments = documents.filter(d => d.type_document === 'cv');
  const autresDocuments = documents.filter(d => d.type_document !== 'cv');

  const getTypeLabel = (type) => {
    switch(type) {
      case 'cv': return 'CV';
      case 'justificatif': return 'Justificatif';
      case 'diplome': return 'Diplôme';
      case 'contrat': return 'Contrat';
      case 'attestation': return 'Attestation';
      default: return 'Autre';
    }
  };

  return (
    <div className="space-y-6">
      {/* Section CV */}
      <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <User className="w-5 h-5 text-blue-600" />
            CV
          </CardTitle>
          <Dialog open={openCV} onOpenChange={setOpenCV}>
            <DialogTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700 text-white rounded-xl">
                <Upload className="w-4 h-4 mr-2" />
                Téléverser CV
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader>
                <DialogTitle>Téléverser votre CV</DialogTitle>
                <DialogDescription>Formats acceptés : PDF, JPG, PNG</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Fichier CV</Label>
                  <Input
                    type="file"
                    onChange={handleFileChange}
                    className="rounded-xl"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenCV(false)} className="rounded-xl">
                  Annuler
                </Button>
                <Button onClick={() => handleUpload('cv')} disabled={loading} className="bg-blue-600 hover:bg-blue-700 rounded-xl">
                  {loading ? 'Téléversement...' : 'Téléverser'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {cvDocuments.length > 0 ? (
            <div className="space-y-2">
              {cvDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-blue-50 rounded-xl border border-blue-200"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <div>
                      <div className="font-medium text-slate-900">{doc.nom_fichier}</div>
                      <div className="text-xs text-blue-600 font-medium">Curriculum Vitae</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(doc)}
                      className="rounded-xl border-blue-200 text-blue-700 hover:bg-blue-100"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(doc.id)}
                      className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
              <User className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">Aucun CV téléversé</p>
              <p className="text-sm text-slate-400">Cliquez sur "Téléverser CV" pour ajouter votre curriculum vitae</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Section Autres Documents */}
      <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <FolderOpen className="w-5 h-5 text-emerald-600" />
            Autres Documents
          </CardTitle>
          <Dialog open={openAutre} onOpenChange={setOpenAutre}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl">
                <Upload className="w-4 h-4 mr-2" />
                Ajouter un document
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-3xl">
              <DialogHeader>
                <DialogTitle>Téléverser un document</DialogTitle>
                <DialogDescription>Justificatifs, diplômes, attestations...</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Type de document</Label>
                  <Select value={typeDocument} onValueChange={setTypeDocument}>
                    <SelectTrigger className="rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="justificatif">Justificatif d'absence</SelectItem>
                      <SelectItem value="diplome">Diplôme / Certification</SelectItem>
                      <SelectItem value="attestation">Attestation</SelectItem>
                      <SelectItem value="contrat">Contrat de travail</SelectItem>
                      <SelectItem value="autre">Autre document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Fichier</Label>
                  <Input
                    type="file"
                    onChange={handleFileChange}
                    className="rounded-xl"
                    accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpenAutre(false)} className="rounded-xl">
                  Annuler
                </Button>
                <Button onClick={() => handleUpload('autre')} disabled={loading} className="bg-emerald-600 hover:bg-emerald-700 rounded-xl">
                  {loading ? 'Téléversement...' : 'Téléverser'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {autresDocuments.length > 0 ? (
            <div className="space-y-2">
              {autresDocuments.map((doc) => (
                <div
                  key={doc.id}
                  className="flex items-center justify-between p-3 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <div className="flex items-center gap-3">
                    <FileText className="w-5 h-5 text-emerald-600" />
                    <div>
                      <div className="font-medium text-slate-900">{doc.nom_fichier}</div>
                      <div className="text-xs text-slate-500">{getTypeLabel(doc.type_document)}</div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(doc)}
                      className="rounded-xl"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(doc.id)}
                      className="rounded-xl border-red-200 text-red-600 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6 bg-slate-50 rounded-xl border-2 border-dashed border-slate-200">
              <FolderOpen className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p className="text-slate-500">Aucun document</p>
              <p className="text-sm text-slate-400">Ajoutez des justificatifs, diplômes ou autres documents</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default DocumentUploader;
